import React, { useEffect, useState } from 'react';
import {
  Button, Table, Form, InputGroup,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../Components/Error';
import ListPager from '../../Components/ListPager';
import PartnerInputModal from './Components/PartnerInputModal';
import PartnerPaymentsModal from './Components/PartnerPaymentsModal';
import PartnerWaitingTimesModal from './Components/PartnerWaitingTimesModal';
import CounterpartyDocumentsModal from '../../Components/KnowledgeBase/CounterpartyDocumentsModal';
import useModalState from '../../Hooks/useModalState';
import UserService from '../../Services/UserService';
import Permissions from '../../Utils/Permissions';
import usePagedList from '../../Hooks/usePagedList';
import PartnersService from '../../Services/PartnersService';
import { needsWaitingTimes } from '../../Utils/freeWaiting';

/**
 * What this screen keeps in the URL beyond the search, the page and the page
 * size every paged list keeps: whether it is showing only the channels with
 * no agreed waiting times. There are 158 active partners and almost none of
 * them have terms, so the amber flag on its own is a wall rather than a
 * worklist; this turns it into one that can be worked down.
 */
const PARTNERS_VIEW = { unset: false };

/** The screen's own search and filters, over one partner row. */
const matchesPartner = (partner, { search, unset }) => {
  if (unset && !needsWaitingTimes(partner)) return false;

  const query = search.trim().toLowerCase();
  if (!query) return true;

  return [partner.name, partner.email, partner.phone_number, partner.website, partner.Id]
    .some((field) => (field || '').toString().toLowerCase().includes(query));
};

function Partners() {
  const [partners, setPartners] = useState([]);
  // Whether the collection has actually arrived. An empty list mid-fetch must
  // not be mistaken for "page 3 does not exist" — see usePagedList.
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  // Holds the partner whose payments modal is currently open, or null.
  const [showPaymentsModalFor, setShowPaymentsModalFor] = useState(null);

  // New or existing order change
  const showPartnerInputModal = useModalState();
  // The free-waiting terms agreed with this channel, on their own screen.
  const showWaitingTimesModal = useModalState();
  // This channel's own paperwork, pre-scoped to them.
  const showDocumentsModal = useModalState();

  // Gates the Documents button AND the upload/delete inside the modal.
  const canManageKnowledgeBase = UserService.getInstance()
    .canAccessModule(Permissions.MANAGE_AI);

  const fetchPartners = async () => {
    try {
      const fetchedPartners = await PartnersService.fetchPartners();
      setPartners(fetchedPartners?.data?.data);
    } catch (err) {
      setError(error);
    } finally {
      setLoaded(true);
    }
  };

  const debouncedFetchPartners = _.debounce(() => {
    fetchPartners();
  }, 500);

  useEffect(() => {
    debouncedFetchPartners();
    return () => debouncedFetchPartners.cancel();
  }, []);

  const {
    view, rows, setFilter, setPage, setSize, pagination,
  } = usePagedList({
    items: partners,
    matches: matchesPartner,
    loaded,
    defaults: PARTNERS_VIEW,
  });

  /** Why the table is empty, which is not always "there are no partners". */
  const emptyMessage = () => {
    if (view.search) return 'No partners match your search.';
    if (view.unset) return 'Every channel has agreed waiting times.';
    return 'No partners found.';
  };

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              Partners
              {' '}
              <span className="hero-highlight">Management</span>
            </h1>
            <p className="lead text-muted mb-4">
              Manage business partners and collaborations
            </p>
            <div className="hero-actions">
              <Button
                onClick={() => {
                  showPartnerInputModal.show({});
                }}
                size="lg"
                variant="primary"
              >
                <i className="fa fa-plus me-2" />
                Add New Partner
              </Button>
            </div>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-handshake fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      <Error error={error} setError={setError} />

      <div className="list-search mb-3">
        <InputGroup>
          <InputGroup.Text className="list-search-icon">
            <i className="fa fa-search" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search partners by name, email, phone…"
            value={view.search}
            onChange={(e) => setFilter({ search: e.target.value })}
          />
          {view.search && (
            <Button variant="outline-secondary" onClick={() => setFilter({ search: '' })} aria-label="Clear search">
              <i className="fa fa-times" />
            </Button>
          )}
        </InputGroup>
      </div>

      <Form.Check
        type="switch"
        id="partners-waiting-times-unset"
        className="mb-3"
        label="Only channels with no agreed waiting times"
        checked={view.unset}
        onChange={(e) => setFilter({ unset: e.target.checked })}
      />

      <Table responsive striped bordered hover className="mt-2 responsive-card-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>E-mail address</th>
            <th>Website</th>
            <th>Phone number</th>
            <th>Active</th>
            <th>Waiting Times</th>
            <th>Payments</th>
            <th>UUID</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={9} className="text-center text-muted py-4">
                {emptyMessage()}
              </td>
            </tr>
          )}
          {rows.map((partner) => (
            <tr key={partner.id}>
              <td data-label="Name">{partner.name}</td>
              <td data-label="E-mail address">{partner.email}</td>
              <td data-label="Website">{partner.website}</td>
              <td data-label="Phone number">{partner.phone_number}</td>
              {/* Flagged on the list as well as on the booking form, so a
                  batch of unset channels can be fixed in one sitting rather
                  than discovered one booking at a time. */}
              <td data-label="Active">{partner.active ? 'Yes' : 'No'}</td>
              <td data-label="Waiting Times">
                <Button
                  size="sm"
                  variant={needsWaitingTimes(partner) ? 'outline-warning' : 'outline-secondary'}
                  onClick={() => {
                    showWaitingTimesModal.show(partner);
                  }}
                >
                  <i className="fa fa-hourglass-half me-2" />
                  {needsWaitingTimes(partner) ? 'Not set' : 'Waiting Times'}
                </Button>
              </td>
              <td data-label="Payments">
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    setShowPaymentsModalFor(partner);
                  }}
                >
                  <i className="fa fa-receipt me-2" />
                  Payments
                </Button>
              </td>
              <td data-label="UUID">{partner.Id}</td>
              <td>
                <div className="d-flex gap-2 flex-wrap">
                  {canManageKnowledgeBase && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        showDocumentsModal.show(partner);
                      }}
                      title="Documents the assistant answers about this partner from"
                    >
                      <i className="fa fa-folder-open me-2" />
                      Documents
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => {
                      showPartnerInputModal.show(partner);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <ListPager
        pagination={pagination}
        onPageChange={setPage}
        onLimitChange={setSize}
      />

      {/* The modals are the screen's, not the row's: each holds the partner it
          was opened with, so turning a page underneath one leaves it alone. */}
      {showWaitingTimesModal.isVisible && (
      <PartnerWaitingTimesModal
        originalPartner={showWaitingTimesModal.selectedObject}
        show={showWaitingTimesModal.isVisible}
        fetchPartners={fetchPartners}
        handleClose={showWaitingTimesModal.hide}
      />
      ) }

      {showPartnerInputModal.isVisible && (
      <PartnerInputModal
        originalPartner={showPartnerInputModal.selectedObject}
        show={showPartnerInputModal.isVisible}
        fetchPartners={fetchPartners}
        handleClose={showPartnerInputModal.hide}
      />
      ) }

      <PartnerPaymentsModal
        show={!!showPaymentsModalFor}
        onHide={() => setShowPaymentsModalFor(null)}
        partner={showPaymentsModalFor || { id: '', name: '' }}
      />

      {/* The same uploader and document list as the Knowledge Base screen,
          pre-scoped to this partner so nothing has to be picked. */}
      {showDocumentsModal.isVisible && (
      <CounterpartyDocumentsModal
        show={showDocumentsModal.isVisible}
        handleClose={showDocumentsModal.hide}
        kind="partner"
        counterparty={showDocumentsModal.selectedObject}
        canManage={canManageKnowledgeBase}
      />
      ) }
    </>
  );
}

export default Partners;
